import { Lock, Shield, User } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { useState, useEffect } from "react";
import { useAdminAuth } from "../context/adminAuth";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription } from "../../components/ui/alert";

const AdminLogin = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const navigate = useNavigate();

  const { loginAdmin, isAuthenticated, loading, adminUser, error } = useAdminAuth();

  // Debug: Log authentication state
  useEffect(() => {
    console.log("Auth State:", { isAuthenticated, loading, adminUser, error });
    console.log("Token in localStorage:", localStorage.getItem("admintoken"));
  }, [isAuthenticated, loading, adminUser, error]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !loading) {
      console.log("Already authenticated, redirecting to dashboard");
      navigate("/admin/dashboard");
    }
  }, [isAuthenticated, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    console.log("Attempting login with:", { username, password: "***" });

    try {
      const response = await loginAdmin(username, password);
      console.log("Login response:", response);

      if (response.success) {
        console.log("Login successful, navigating to dashboard");
        // Small delay to ensure state is updated
        setTimeout(() => {
          window.location.href = "/admin/dashboard";
        }, 100);
      } else {
        console.log("Login failed:", response.error);
        setErrorMsg(response.error || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);
      setErrorMsg(err.message || "Login failed.");
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading if auth is still checking
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-igbanwo-mint-50 to-igbanwo-mint-100">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-igbanwo-mint-50 to-igbanwo-mint-100 px-4">
      <div className="max-w-md w-full space-y-8">
        {/* Debug Info */}
        {/* <div className="text-xs text-gray-500 bg-gray-100 p-2 rounded">
          Debug: Auth={isAuthenticated.toString()}, Loading={loading.toString()}, 
          User={adminUser ? "present" : "null"}, Token={localStorage.getItem("admintoken") ? "present" : "null"}
        </div> */}

        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-igbanwo-mint rounded-full flex items-center justify-center mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Admin Portal</h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to access the administration panel
          </p>
        </div>

        {/* Login Form */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-center text-xl">
              Administrator Login
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label
                    htmlFor="username"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Username
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      required
                      className="pl-10"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      placeholder="Enter your username"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div>
                  <Label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      required
                      className="pl-10"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              {(errorMsg || error) && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMsg || error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Signing In...
                  </div>
                ) : (
                  "Sign In"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Security Notice */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            This is a secure area. All access attempts are logged and monitored.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;